import { useMemo } from 'react';

import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import { LINK_FARM, MARKETING_LINKS } from '../links';
import type { LinkFarmAppCategory, LinkFarmCategory } from '../types';
import type { LinkFarmLink } from '../types';

interface HookOptions {
  internal: boolean;
  category?: LinkFarmCategory | LinkFarmAppCategory;
}

export function useLinkFarmLinks({ internal, category }: HookOptions) {
  const allFlagValues = useFeatureFlags(Object.values(FEATURE_FLAGS));
  const allFlags = Object.fromEntries(
    Object.entries(FEATURE_FLAGS).map(([, key], index) => [
      key,
      allFlagValues[index],
    ]),
  );

  const links = useMemo(() => {
    let targetLinks: LinkFarmLink[] = [];

    if (category) {
      // In the future, if we have other links that are outside LINK_FARM, we
      // can check for them here as well.
      if (category === 'marketing') {
        targetLinks = MARKETING_LINKS;
      } else {
        targetLinks = LINK_FARM[category] || [];
      }
    } else {
      // All possible links. In the future, if we have other links that are
      // outside LINK_FARM, then we can concat them here as well.
      targetLinks = Object.values(LINK_FARM).flat().concat(MARKETING_LINKS);
    }

    return targetLinks.filter((item) => {
      // Filter out any links that are feature flagged and have the flag
      // currently turned off.
      if (item.featureFlagKey && !allFlags[item.featureFlagKey]) {
        return false;
      }

      // Internal items.
      if (internal && item.isInternal) {
        return true;
      }

      // External items.
      return !internal && !item.isInternal;
    });
  }, [allFlags, category, internal]);

  return links;
}
